/**
 * PagedGrid and Pager — a grid that shows one page of a longer list at a time,
 * turning itself every few seconds, with a quiet pager that can live anywhere
 * on the page. Product owner, 1 October 2026, comparing the homepage with the
 * brand reference: *"can't we merge both together"* — the reference's 3×2 grid
 * of cards, and the carousel's auto-advance over the twelve items per kind.
 *
 * - **The layout is the caller's** (`className` on the grid, `perPage` from
 *   `usePagedGridPerPage` or the caller's own rule), as with `Carousel`: a
 *   shared component that hard-coded "three columns" would be one more place
 *   deciding layout for a screen.
 * - **The state is `usePagedGrid`'s**, created by the caller and handed to both
 *   halves. See that file for the pause rules — they are `Carousel`'s, as the
 *   owner approved them.
 * - **Every item stays mounted.** Off-page items are `hidden` *and* `inert`, so
 *   a page turn never re-fetches or re-mounts a tile and Tab never lands on a
 *   card nobody can see.
 * - **The region is `aria-live="off"`**: announcing every five-second turn
 *   would make a screen reader unusable. A page the *reader* turned is
 *   announced instead, by the `Pager` (Task 4 review I-2).
 * - **The page change is a short fade-in** of the incoming page (`page-in`,
 *   220ms, opacity only), under `motion-safe:` — reduced motion swaps the page
 *   instantly. A `hidden` element that becomes visible restarts its CSS
 *   animation, which is all the turn needs: no timers, no exit state.
 *
 * `Carousel` is kept, with its own tests; this is its sibling for a grid.
 */
import { cn } from './cn'
import type { PagedGridController } from './usePagedGrid'

export type PagedGridProps<Item> = {
  controller: PagedGridController
  items: readonly Item[]
  keyFor: (item: Item) => string
  renderItem: (item: Item) => React.ReactNode
  /** Accessible name of the region — the collection's own title. */
  label: string
  /** The grid's columns and gaps, e.g. `grid gap-6 sm:grid-cols-2 md:grid-cols-3`. */
  className?: string | undefined
}

export function PagedGrid<Item>({
  controller,
  items,
  keyFor,
  renderItem,
  label,
  className,
}: PagedGridProps<Item>) {
  if (items.length === 0) return null

  const start = controller.page * controller.perPage
  const end = start + controller.perPage

  return (
    <div role="group" aria-label={label} aria-live="off" {...controller.pauseHandlers}>
      <ul className={cn('grid', className)}>
        {items.map((item, index) => {
          const onPage = index >= start && index < end
          return (
            <li
              key={keyFor(item)}
              hidden={!onPage}
              inert={onPage ? undefined : true}
              // `h-full` on the tile needs a cell that stretches: grid items do
              // by default, and the `li` passes its height straight through.
              className="min-w-0 motion-safe:animate-page-in"
            >
              {renderItem(item)}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export type PagerProps = {
  controller: PagedGridController
  previousLabel: string
  nextLabel: string
  /** What the reader sees between the two buttons — "1 / 3". */
  status: string
  /** What a screen reader hears instead — "Page 1 of 3". */
  statusLabel: string
  className?: string | undefined
}

/**
 * The quiet pager: two text-style icon buttons around "1 / 3". Renders nothing
 * when everything fits on one page — controls that do nothing are noise.
 *
 * Unavailable at either end rather than wrapping, as the carousel's arrows
 * were: a press past the last page that jumped to the first would read as a
 * glitch. Auto-advance alone wraps, because nobody is steering it.
 *
 * ⚠️ **Never the native `disabled`** (Task 4 review I-1). Pressing "Next" onto
 * the last page disables the very button under focus; the browser then drops
 * focus to `<body>` without a `blur` — the keyboard reader loses their place
 * (WCAG 2.4.3) and the grid's "focus is inside" pause sticks. `aria-disabled`
 * plus a no-op press (`usePagedGrid`'s `previous`/`next` check the ends) keeps
 * the button focusable and announced as unavailable.
 *
 * **What a screen reader hears.** The visible "1 / 3" is hidden; the sentence
 * "Page 1 of 3" stands in for it. Once the reader has pressed a button
 * (`controller.steered` — auto-advance is then off for good), the sentence
 * moves into a polite live region, so each press is announced and no
 * automatic turn ever is. The region is mounted empty from the start: one
 * that appears together with its text is not reliably announced. The sentence
 * is in exactly one of the two places at a time, so browsing by line never
 * reads it twice.
 */
export function Pager({
  controller,
  previousLabel,
  nextLabel,
  status,
  statusLabel,
  className,
}: PagerProps) {
  if (controller.pageCount <= 1) return null

  return (
    <div className={cn('flex items-center gap-1', className)} {...controller.pauseHandlers}>
      <PagerButton
        label={previousLabel}
        disabled={controller.atStart}
        onClick={controller.previous}
        direction="previous"
      />
      {/* Visible "1 / 3" is hidden from assistive tech, which hears the
          sentence instead; `tabular-nums` so the width does not twitch as the
          page turns. */}
      <span
        aria-hidden="true"
        className="min-w-[3.25rem] text-center text-[13px] text-fg-muted tabular-nums"
      >
        {status}
      </span>
      {controller.steered ? null : <span className="sr-only">{statusLabel}</span>}
      <span className="sr-only" aria-live="polite" aria-atomic="true">
        {controller.steered ? statusLabel : null}
      </span>
      <PagerButton
        label={nextLabel}
        disabled={controller.atEnd}
        onClick={controller.next}
        direction="next"
      />
    </div>
  )
}

function PagerButton({
  label,
  disabled,
  onClick,
  direction,
}: {
  label: string
  disabled: boolean
  onClick: () => void
  direction: 'previous' | 'next'
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-disabled={disabled}
      onClick={onClick}
      className={cn(
        // 32px square: past the 24px WCAG 2.2 target minimum, quiet enough to
        // sit beside 13px text. No border — the reference's filter row is text.
        'flex size-8 items-center justify-center rounded-[5px] text-fg-muted transition-colors',
        'hover:bg-hover hover:text-fg',
        'aria-disabled:cursor-not-allowed aria-disabled:opacity-40',
        'aria-disabled:hover:bg-transparent aria-disabled:hover:text-fg-muted',
      )}
    >
      {/* `rtl:rotate-180`: "previous" points toward the start of reading
          order, which is right in Arabic. Plain CSS on `dir`, so this works
          without the locale provider mounted. */}
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="size-4 rtl:rotate-180">
        <path
          d={direction === 'previous' ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'}
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  )
}
